import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import ColegioService from '../services/ColegioService';
import { useAuth } from '../contexts/AuthContext';
import aulaService from '../services/AulaService';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import SolicitudesDropdown from './common/SolicitudesDropdown';
import UserSessionMenu from './common/UserSessionMenu';
import { hasOpenAIChatsEnabled, isFeatureEnabled } from '../utils/adminMenu';
import ProfessorMenu from './common/menus/ProfessorMenu';
import DataTable from './common/DataTable';
import AulaStatsModal from './common/AulaStatsModal';
import AulaRecursosModal from './common/AulaRecursosModal';

const hasModuloAlumnosEnabled = (flags) =>
  isFeatureEnabled(flags, 'ModuloAlumnos');

const hasAgentesClasicosEnabled = (flags) =>
  isFeatureEnabled(flags, 'PermiteAgentesClasicosGPT');

// Por defecto permitimos mensajes si no hay flags cargados (fallback true)
const hasMensajesAulaEnabled = (flags) => {
  if (!Array.isArray(flags) || flags.length === 0) return true;
  return isFeatureEnabled(flags, 'PermiteEnviarMensajesEnAula');
};
const hasArchivosAulaEnabled = (flags) => {
  if (!Array.isArray(flags) || flags.length === 0) return true;
  return isFeatureEnabled(flags, 'PermiteSubirArchivosAlAula');
};

const DEFAULT_ITEMS_PER_PAGE = 10;
const getRange = (page, pageSize) => {
  const take = Math.max(Number(pageSize) || DEFAULT_ITEMS_PER_PAGE, 1);
  const start = Math.max(page - 1, 0) * take;
  return { start, end: start + take - 1 };
};

const MyAulas = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [aulas, setAulas] = useState([]);
  const [aulasTotal, setAulasTotal] = useState(0);
  const [aulasPage, setAulasPage] = useState(1);
  const [aulasPageSize, setAulasPageSize] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [search, setSearch] = useState('');
  const [statsModal, setStatsModal] = useState({ open: false, aula: null });
  const [resourcesModal, setResourcesModal] = useState({ open: false, aula: null });
  const [toggleLoadingId, setToggleLoadingId] = useState(null);

  const activeColegio = useMemo(() => ColegioService.getActiveColegio(), []);
  const activeColegioId = useMemo(() => ColegioService.getActiveColegioId(), []);
  const activeColegioName = activeColegio?.nombre || activeColegio?.name || activeColegio?.descripcion || 'Colegio';
  const featureFlags = useMemo(() => ColegioService.getFeatureFlags(), []);
  const hasOpenAIChats = useMemo(() => hasOpenAIChatsEnabled(featureFlags), [featureFlags]);
  const hasModuloAlumnos = useMemo(() => hasModuloAlumnosEnabled(featureFlags), [featureFlags]);
  const hasAgentesClasicos = useMemo(() => hasAgentesClasicosEnabled(featureFlags), [featureFlags]);
  const hasMensajesAula = useMemo(() => hasMensajesAulaEnabled(featureFlags), [featureFlags]);
  const hasArchivosAula = useMemo(() => hasArchivosAulaEnabled(featureFlags), [featureFlags]);
  const hasPizarraAula = useMemo(() => {
    if (!Array.isArray(featureFlags) || featureFlags.length === 0) return true;
    return isFeatureEnabled(featureFlags, 'PermitePizarraCompartidaEnAula');
  }, [featureFlags]);
  const hasChatAula = useMemo(() => {
    if (!Array.isArray(featureFlags) || featureFlags.length === 0) return true;
    return isFeatureEnabled(featureFlags, 'PermiteChatGrupalEnAula');
  }, [featureFlags]);
  const hasVideollamadaAula = useMemo(() => {
    if (!Array.isArray(featureFlags) || featureFlags.length === 0) return true;
    return isFeatureEnabled(featureFlags, 'PermiteIniciarVideoLlamadaEnAula');
  }, [featureFlags]);
  const hasLinksAula = true;
  const hasRecursosAula = hasMensajesAula || hasArchivosAula || hasAgentesClasicos || hasOpenAIChats || hasLinksAula || hasPizarraAula || hasChatAula || hasVideollamadaAula;
  const canCreateAula = Boolean(user?.permiteCrearAula);

  const loadAulas = useCallback(async () => {
    if (!activeColegioId || !hasModuloAlumnos) return;

    setIsLoading(true);
    setError('');
    try {
      const { start, end } = getRange(aulasPage, aulasPageSize);
      const response = await aulaService.getMisAulas({
        idColegio: activeColegioId,
        start,
        end,
        search: search.trim(),
        includeTotal: true,
      });
      const items = Array.isArray(response?.items) ? response.items : Array.isArray(response) ? response : [];
      setAulas(items);
      setAulasTotal(response?.total ?? items.length);
    } catch (err) {
      setError(err.message || 'No pudimos cargar las aulas.');
      setAulas([]);
      setAulasTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [activeColegioId, hasModuloAlumnos, aulasPage, aulasPageSize, search]);

  useEffect(() => {
    loadAulas();
  }, [loadAulas]);

  useEffect(() => {
    setAulasPage(1);
  }, [search]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(aulasTotal / Math.max(aulasPageSize, 1)));
    if (aulasPage > totalPages) {
      setAulasPage(totalPages);
    }
  }, [aulasPage, aulasPageSize, aulasTotal]);

  const handleToggleAula = useCallback(async (aulaId, activo) => {
    try {
      setToggleLoadingId(aulaId);
      await aulaService.updateAulaActivoProfesor(aulaId, !activo);
      setAulas((prev) =>
        prev.map((x) =>
          (x.id || x.Id) === aulaId ? { ...x, activo: !activo, Activo: !activo } : x
        )
      );
    } catch (e) {
      window.alert(e.message || 'No pudimos actualizar el estado.');
    } finally {
      setToggleLoadingId(null);
    }
  }, []);

  const handleAulaUpdated = useCallback((updatedAula) => {
    const updatedId = updatedAula?.id || updatedAula?.Id;
    if (!updatedId) return;
    setResourcesModal((prev) => (
      prev.aula && (prev.aula.id || prev.aula.Id) === updatedId
        ? { ...prev, aula: { ...prev.aula, ...updatedAula } }
        : prev
    ));
    setAulas((prev) =>
      prev.map((item) => ((item.id || item.Id) === updatedId ? { ...item, ...updatedAula } : item))
    );
  }, []);

  const aulasColumns = useMemo(() => ([
    {
      key: 'nombre',
      title: 'Nombre',
      render: (_, aula) => {
        const nombre = aula.nombre || aula.Nombre || 'Aula';
        const descripcion = aula.descripcion || aula.Descripcion || '';
        return (
          <div className="prof-name">
            <span className="name">{nombre}</span>
            {descripcion && <span className="muted">{descripcion}</span>}
          </div>
        );
      },
    },
    {
      key: 'codigo',
      title: 'Código',
      render: (_, aula) => aula.nicknameUrl || aula.NicknameUrl || aula.codigoSuscripcion || aula.CodigoSuscripcion || '—',
    },
    {
      key: 'materia',
      title: 'Materia',
      render: (_, aula) => aula.nombreMateria || aula.NombreMateria || '—',
    },
    {
      key: 'curso',
      title: 'Curso',
      render: (_, aula) => aula.nombreCurso || aula.NombreCurso || '—',
    },
    {
      key: 'ciclo',
      title: 'Ciclo',
      className: 'text-center width-sm',
      render: (_, aula) => aula.cicloLectivo || aula.CicloLectivo || '—',
    },
    {
      key: 'estado',
      title: 'Estado',
      className: 'text-center width-sm',
      render: (_, aula) => {
        const activo = typeof aula.activo === 'boolean' ? aula.activo : typeof aula.Activo === 'boolean' ? aula.Activo : true;
        return (
          <span className={`status-chip ${activo ? 'success' : 'danger'}`}>
            {activo ? 'Activa' : 'Inactiva'}
          </span>
        );
      },
    },
    {
      key: 'acciones',
      title: 'Acciones',
      className: 'text-center width-sm',
      sortable: false,
      render: (_, aula) => {
        const aulaId = aula.id || aula.Id;
        const activo = typeof aula.activo === 'boolean' ? aula.activo : typeof aula.Activo === 'boolean' ? aula.Activo : true;
        return (
          <div className="action-buttons">
            {canCreateAula ? (
              <button
                type="button"
                className="action-edit-btn"
                title="Editar aula"
                onClick={() => navigate(`/my-aulas/${aulaId}/editar`)}
              >
                <i className="fas fa-edit"></i>
              </button>
            ) : (
              <span className="muted">—</span>
            )}
            <button
              type="button"
              className="action-stats-btn"
              title="Ver estadísticas del aula"
              onClick={() => setStatsModal({ open: true, aula })}
            >
              <i className="fas fa-chart-line"></i>
            </button>
            {hasRecursosAula && (
              <button
                type="button"
                className="action-resources-btn"
                title="Recursos del aula"
                onClick={() => setResourcesModal({ open: true, aula })}
              >
                <i className="fas fa-layer-group"></i>
              </button>
            )}
            <button
              type="button"
              className={`action-toggle-btn ${activo ? 'danger' : 'success'}`}
              title={activo ? 'Desactivar aula' : 'Activar aula'}
              disabled={toggleLoadingId === aulaId}
              onClick={() => handleToggleAula(aulaId, activo)}
            >
              <i className={`fas ${activo ? 'fa-ban' : 'fa-check-circle'}`} />
            </button>
          </div>
        );
      },
    },
  ]), [canCreateAula, handleToggleAula, hasRecursosAula, navigate, toggleLoadingId]);

  const handleNavigate = (item) => {
    if (!item?.path) return;
    setIsSidebarOpen(false);
    navigate(item.path);
  };

  if (!hasModuloAlumnos) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">Aulas</h1>
            <p className="dashboard-subtitle">El módulo de alumnos aún no está habilitado para este colegio.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`new-dashboard my-assistants ${isSidebarOpen ? 'sidebar-open' : ''}`}>
      {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />}
      <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-graduation-cap"></i>
            <span>aluxIA</span>
          </div>
        </div>

        <ProfessorMenu
          activeId="aulas"
          hasOpenAIChats={hasOpenAIChats}
          hasAgentesClasicos={hasAgentesClasicos}
          hasModuloAlumnos={hasModuloAlumnos}
          onNavigate={handleNavigate}
        />

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <span className="user-role">Profesor</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={() => setIsSidebarOpen((prev) => !prev)}
              title="Mostrar menú"
            >
              <i className="fas fa-bars"></i>
            </button>
            <h1 className="page-title">
              <i className="fas fa-door-open"></i>
              Aulas asignadas
            </h1>
            <p className="page-subtitle">Aulas del colegio {activeColegioName}</p>
          </div>

          <div className="header-right">
            <SolicitudesDropdown />
            <ActiveColegioBadge />
            <UserSessionMenu user={user} onLogout={logout} />
          </div>
        </header>

        <div className="dashboard-content">
          <div className="content-card">
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-door-open"></i>
                <span>Listado de aulas</span>
              </div>
              <div className="professor-actions">
                <div className="professor-search">
                  <i className="fas fa-search" aria-hidden="true" />
                  <input
                    type="text"
                    placeholder="Buscar por aula, código o materia"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                {canCreateAula && (
                  <button className="btn btn-primary" type="button" onClick={() => navigate('/my-aulas/nuevo')}>
                    <i className="fas fa-plus"></i>
                    Crear aula
                  </button>
                )}
              </div>
            </div>

            <div className="card-content professors-table">
              {!isLoading && error && (
                <div className="data-table-empty">
                  <div className="empty-icon">
                    <i className="fas fa-exclamation-triangle"></i>
                  </div>
                  <p>{error}</p>
                </div>
              )}
              {!error && (
                <DataTable
                  data={aulas}
                  columns={aulasColumns}
                  loading={isLoading}
                  loadingMessage="Cargando aulas..."
                  emptyMessage="No hay aulas asignadas."
                  externalPagination
                  externalTotalItems={aulasTotal}
                  externalTotalPages={Math.max(1, Math.ceil(aulasTotal / aulasPageSize))}
                  externalCurrentPage={aulasPage}
                  onPageChange={setAulasPage}
                  onItemsPerPageChange={(nextPageSize) => {
                    setAulasPageSize(nextPageSize);
                    setAulasPage(1);
                  }}
                  defaultItemsPerPage={aulasPageSize}
                />
              )}

            </div>
          </div>
        </div>
      </main>
      {statsModal.open && (
        <AulaStatsModal
          open={statsModal.open}
          aula={statsModal.aula}
          onClose={() => setStatsModal({ open: false, aula: null })}
        />
      )}
      {resourcesModal.open && (
        <AulaRecursosModal
          open={resourcesModal.open}
          aula={resourcesModal.aula}
          hasMensajes={hasMensajesAula}
          hasArchivos={hasArchivosAula}
          hasAsistentes={hasAgentesClasicos}
          hasLinks={hasLinksAula}
          globalAvailability={{
            mostrarArchivos: hasArchivosAula,
            mostrarLinks: hasLinksAula,
            mostrarPizarra: hasPizarraAula,
            mostrarChat: hasChatAula,
            mostrarVideollamada: hasVideollamadaAula,
            mostrarForo: hasMensajesAula,
            mostrarAsistentesIA: hasAgentesClasicos || hasOpenAIChats
          }}
          resourceVisibilityService={(id, payload) => aulaService.updateAulaRecursosVisibilidad(id, payload)}
          onAulaUpdated={handleAulaUpdated}
          canUploadFiles
          onClose={() => setResourcesModal({ open: false, aula: null })}
        />
      )}
    </div>
  );
};

export default MyAulas;
